/**
 * Navigation Filter Utility
 * Filters navigation items based on enabled modules
 */

import { isModuleEnabled } from '@/utils/moduleControl'

// Parent menus that should only hide when explicitly disabled (not when children are disabled)
// These are controlled by their own VUE_APP_MODULE_* variable
const PARENT_ONLY_MODULES = {
  Localisation: 'localisation',
  Utilisateurs: 'users',
  Paramètres: 'settings',
  Reporting: 'reporting',
}

const MODULE_ROUTE_MAP = {
  // Parent menu titles to modules (these hide entire section when disabled)
  'Demande partenaire': 'requestPartner',
  'Rechercher un colis': 'orderSearchHistory',
  'Statuts non mis à jour': 'staleOutsourcingStatus',
  'État de synchronisation': 'synchronization',
  'Correction références': 'synchronization',
  'Correction BL': 'synchronization',
  Colis: 'orders',
  Bordereau: 'deliveryNotes',
  Pickup: 'pickups',
  Inventaire: 'inventory',
  'Run sheet': 'roadmaps',
  'Bon de transfert': 'transferVouchers',
  "Bon d'échange": 'exchange',
  Débrief: 'payments',
  'Traitement anomalie': 'returnVouchers',
  'Bon A rembourser': 'toBeReimbursedVouchers',
  'Retour inter-agence': 'returnVouchers',
  Finance: 'invoices',
  Dépenses: 'expenses',
  Support: 'support',
  'Payment sous traitance': 'paymentSousTraitance',
  'Suivi sous-traitance': 'manifestsPickup',
  // Reporting sub-items
  'reporting-weekly-payments': 'reportingWeeklyPayments',
  'reporting-states': 'reportingStates',
  'reporting-supplier-performance': 'reportingSupplierPerformance',
  'reporting-top-drivers': 'reportingTopDrivers',
  'dashboard-banners': 'dashboardBanners',
  'my-wallet': 'wallets',
  'transaction-show': 'wallets',
  'wallet-transactions': 'wallets',
  'wallet-show': 'wallets',
  wallets: 'wallets',
  'support-order-tracking': 'orders',
  'reporting-turnover': 'reportingTurnover',
  parameters: 'settings',
  'dashboard-banner-parameters': 'settings',
  'roadmap-print-parameters': 'settings',
  'supplier-bordereau-display-parameters': 'settings',
  'company-bordereau-display-parameters': 'settings',
  'delivery-note-print-note-parameters': 'settings',
  'inventory-parameters': 'settings',
  // Individual routes to modules
  repositories: 'repositories',
  cars: 'cars',
  suppliers: 'suppliers',
  'delivery-mans': 'deliveryMans',
  pickupeurs: 'pickupeurs',
  managers: 'managers',
  staff: 'staff',
  'orders-index': 'orders',
  'orders-deleted': 'orders',
  'order-add': 'orders',
  'order-quick-add': 'orders',
  'orders-search-history': 'orderSearchHistory',
  'order-status': 'orders',
  'stale-outsourcing-status': 'staleOutsourcingStatus',
  'sync-state-orders': 'synchronization',
  'sync-reference-correction': 'synchronization',
  'echange-order': 'exchange',
  'orders-quick-edit': 'orders',
  'inventory-dashboard': 'inventory',
  'inventory-history': 'inventory',
  'pickup-list': 'pickups',
  'pickup-roadmaps': 'pickups',
  'create-pickup-roadmap': 'pickups',
  'delivery-note-list': 'deliveryNotes',
  'roadmap-list': 'roadmaps',
  'roadmap-add': 'roadmaps',
  'reception-transfert': 'transferVouchers',
  'bon-transfert-list': 'transferVouchers',
  'bon-transfert-add': 'transferVouchers',
  'bon-echange-list': 'exchange',
  'bon-echange-add': 'exchange',
  'manifests-list': 'manifestsPickup',
  'manifest-create': 'manifestsPickup',
  'manifest-detail': 'manifestsPickup',
  'exchange-manifest-list': 'exchangeManifests',
  'exchange-manifest-create': 'exchangeManifests',
  'exchange-manifest-detail': 'exchangeManifests',
  'payment-list': 'payments',
  'payment-add': 'payments',
  'bon-retour-list': 'returnVouchers',
  'bon-retour-add': 'returnVouchers',
  'bon-a-rembourser-list': 'toBeReimbursedVouchers',
  'bons-remboursement-add': 'toBeReimbursedVouchers',
  'bons-a-rembourser-change-status': 'toBeReimbursedVouchers',
  'bon-retour-list-inter-agence': 'returnVouchers',
  'bon-retour-inter-agence-add': 'returnVouchers',
  'invoice-list': 'invoices',
  'invoice-add': 'invoices',
  'invoice-list-direx': 'invoices',
  'expenses-index': 'expenses',
  'expenses-create': 'expenses',
  'vendors-index': 'expenses',
  'categories-index': 'expenses',
  'payment-sous-traitance-list': 'paymentSousTraitance',
  'payment-sous-traitance-create': 'paymentSousTraitance',
  'payment-sous-traitance-detail': 'paymentSousTraitance',
  'outsourcing-pricing': 'outsourcingPricing',
  'supplier-list': 'suppliers',
  'delivery-man-list': 'deliveryMans',
  'pickupeur-list': 'pickupeurs',
  'manager-list': 'managers',
  'staff-list': 'staff',
  'governorate-list': 'governorates',
  'delegation-list': 'delegations',
  'localities-list': 'localities',
  'return-reason-type-list': 'returnReasonTypes',
  'repository-list': 'repositories',
  'car-list': 'cars',
  'weight-list': 'weights',
  'synchronization-list': 'synchronization',
  'ticket-category-list': 'ticketCategories',
  SupportTicketList: 'support',
  'support-driver-ticket-list': 'support',
  RedeliveryRequestList: 'support',
  PriceChangeRequestList: 'support',
  ProfileChangeRequestList: 'support',
  AccountDisableRequestList: 'support',
}

/**
 * Filter navigation items based on enabled modules
 * @param {array} navItems - Array of navigation items
 * @returns {array} - Filtered navigation items
 */
export const filterNavigation = navItems => navItems
  .map(item => {
    // Check if this is a parent-only module (like Localisation, Utilisateurs, Paramètres)
    // These only hide when their specific parent module is disabled
    if (item.title && PARENT_ONLY_MODULES[item.title]) {
      const parentModuleKey = PARENT_ONLY_MODULES[item.title]
      if (!isModuleEnabled(parentModuleKey)) {
        return null // Hide entire parent section only if parent module is disabled
      }
    } else if (item.title && MODULE_ROUTE_MAP[item.title]) {
      // Check if parent menu item title should be disabled (for menus without individually controllable children)
      const moduleKey = MODULE_ROUTE_MAP[item.title]
      if (!isModuleEnabled(moduleKey)) {
        return null // Hide entire parent section
      }
    }

    // Check if item's route module is disabled
    if (item.route && typeof item.route === 'string') {
      const moduleKey = MODULE_ROUTE_MAP[item.route]
      if (moduleKey && !isModuleEnabled(moduleKey)) {
        return null // Hide this item
      }
    }

    // Recursively filter children
    if (item.children && item.children.length > 0) {
      const filteredChildren = item.children
        .map(child => {
          if (child.route && typeof child.route === 'string') {
            const moduleKey = MODULE_ROUTE_MAP[child.route]
            if (moduleKey && !isModuleEnabled(moduleKey)) {
              return null // Hide this child
            }
          }
          return child
        })
        .filter(child => child !== null)

      // If all children are filtered out, don't show parent
      if (filteredChildren.length === 0) {
        return null
      }

      return {
        ...item,
        children: filteredChildren,
      }
    }

    return item
  })
  .filter(item => item !== null)

export default {
  filterNavigation,
  MODULE_ROUTE_MAP,
}
